/**
 * FormrManager - Core form definition and management system
 * 
 * CHANGE HISTORY:
 * - 251023_1954 [pending] danielbeer: Initial creation with Mappr, Taggr, and routing integration
 * 
 * Manages form schemas, field mappings, auto-tagging, and data routing
 */

import type {
  FormConfig,
  Form,
  SubmissionResult,
  RouteResult,
  FormrConfig,
  FormrEvents,
  MappingConfig,
  TaggingConfig
} from '../types';

export class FormrManager {
  private forms: Map<string, Form> = new Map();
  private config: FormrConfig;
  private events: FormrEvents;
  
  // Integration dependencies
  private mappr: any;
  private taggr: any;
  private integratr: any;
  private actr: any;
  private validatr: any;
  private loggr: any;
  
  constructor(config: FormrConfig = {}, events: FormrEvents = {}) {
    this.config = config;
    this.events = events;
    
    // Set up integrations
    this.mappr = config.mappr;
    this.taggr = config.taggr;
    this.integratr = config.integratr;
    this.actr = config.actr;
    this.validatr = config.validatr;
    this.loggr = config.loggr;
    
    // Register Actr actions if available
    if (this.actr) {
      this.registerActions();
    }
    
    this.log('info', 'FormrManager initialized');
  }
  
  /**
   * Create a new form
   */
  createForm(id: string, config: FormConfig): Form {
    if (this.forms.has(id)) {
      throw new Error(`Form already exists: ${id}`);
    }
    
    const form: Form = {
      id,
      config: {
        ...config,
        id,
        createdAt: Date.now(),
        updatedAt: Date.now()
      },
      data: this.getDefaultData(config),
      errors: {},
      touched: {},
      isValid: false,
      isSubmitting: false
    };
    
    this.forms.set(id, form);
    this.log('info', `Form created: ${id}`);
    this.events.onFormCreated?.(id);
    
    return form;
  }
  
  /**
   * Get a form by ID
   */
  getForm(id: string): Form | undefined {
    return this.forms.get(id);
  }
  
  /**
   * Update form configuration
   */
  updateForm(id: string, config: Partial<FormConfig>): Form {
    const form = this.forms.get(id);
    if (!form) {
      throw new Error(`Form not found: ${id}`);
    }
    
    form.config = {
      ...form.config,
      ...config,
      updatedAt: Date.now()
    };
    
    this.log('info', `Form updated: ${id}`);
    this.events.onFormUpdated?.(id);
    
    return form;
  }
  
  /**
   * Delete a form
   */
  deleteForm(id: string): boolean {
    const deleted = this.forms.delete(id);
    if (deleted) {
      this.log('info', `Form deleted: ${id}`);
      this.events.onFormDeleted?.(id);
    }
    return deleted;
  }
  
  /**
   * List all forms
   */
  listForms(): Form[] {
    return Array.from(this.forms.values());
  }
  
  /**
   * Set field value
   */
  setFieldValue(formId: string, fieldId: string, value): void {
    const form = this.forms.get(formId);
    if (!form) {
      throw new Error(`Form not found: ${formId}`);
    }
    
    form.data[fieldId] = value;
    form.touched[fieldId] = true;
    
    // Validate field
    const error = this.validateField(form.config, fieldId, value);
    if (error) {
      form.errors[fieldId] = error;
    } else {
      delete form.errors[fieldId];
    }
    
    // Update form validity
    form.isValid = Object.keys(form.errors).length === 0;
    
    this.events.onFieldChanged?.(formId, fieldId, value);
  }
  
  /**
   * Reset form to initial state
   */
  resetForm(formId: string): void {
    const form = this.forms.get(formId);
    if (!form) {
      throw new Error(`Form not found: ${formId}`);
    }
    
    form.data = this.getDefaultData(form.config);
    form.errors = {};
    form.touched = {};
    form.isValid = false;
    form.isSubmitting = false;
    
    this.log('info', `Form reset: ${formId}`);
    this.events.onFormReset?.(formId);
  }
  
  /**
   * Validate entire form
   */
  validateForm(formId: string): Record<string, string> {
    const form = this.forms.get(formId);
    if (!form) {
      throw new Error(`Form not found: ${formId}`);
    }
    
    const errors: Record<string, string> = {};
    
    for (const field of form.config.fields) {
      const error = this.validateField(form.config, field.id, form.data[field.id]);
      if (error) {
        errors[field.id] = error;
      }
    }
    
    form.errors = errors;
    form.isValid = Object.keys(errors).length === 0;
    
    if (!form.isValid) {
      this.events.onValidationError?.(formId, errors);
    }
    
    return errors;
  }
  
  /**
   * Submit form
   */
  async submitForm(formId: string): Promise<SubmissionResult> {
    const form = this.forms.get(formId);
    if (!form) {
      throw new Error(`Form not found: ${formId}`);
    }
    
    // Validate
    const errors = this.validateForm(formId);
    if (Object.keys(errors).length > 0) {
      return {
        success: false,
        data: form.data,
        mappedData: null,
        tags: [],
        routes: [],
        error: 'Validation failed'
      };
    }
    
    form.isSubmitting = true;
    
    try {
      const result = await this.processSubmission(formId, form.data);
      form.isSubmitting = false;
      
      this.log('info', `Form submitted: ${formId}`, result);
      this.events.onFormSubmitted?.(formId, result);
      
      return result;
    } catch (error) {
      form.isSubmitting = false;
      this.log('error', `Form submission failed: ${formId}`, error);
      
      return {
        success: false,
        data: form.data,
        mappedData: null,
        tags: [],
        routes: [],
        error: error.message
      };
    }
  }
  
  /**
   * Process form submission (mapping, tagging, routing)
   */
  private async processSubmission(formId: string, data): Promise<SubmissionResult> {
    const form = this.forms.get(formId);
    if (!form) {
      throw new Error(`Form not found: ${formId}`);
    }
    
    const result: SubmissionResult = {
      success: false,
      data,
      mappedData: null,
      tags: [],
      routes: []
    };
    
    try {
      // 1. Map fields via Mappr
      if (form.config.mapping?.enabled) {
        result.mappedData = await this.mapFields(form.config.mapping, data);
        this.log('info', `Fields mapped for form: ${formId}`, result.mappedData);
      }
      
      // 2. Tag data via Taggr
      if (form.config.tagging?.enabled) {
        result.tags = await this.tagData(form.config.tagging, data);
        this.log('info', `Data tagged for form: ${formId}`, result.tags);
      }
      
      // 3. Route data to destinations
      if (form.config.routing) {
        result.routes = await this.routeData(
          form.config.routing,
          result.mappedData || data,
          result.tags,
          formId
        );
        this.log('info', `Data routed for form: ${formId}`, result.routes);
      }
      
      result.success = true;
      return result;
      
    } catch (error) {
      result.error = error.message;
      this.log('error', `Submission processing failed: ${formId}`, error);
      return result;
    }
  }
  
  /**
   * Map fields using Mappr
   */
  private async mapFields(mapping: MappingConfig, data): Promise<any> {
    if (!this.mappr) {
      this.log('warn', 'Mappr not configured, skipping field mapping');
      return data;
    }
    
    // Auto-map fields
    if (mapping.autoMap && mapping.targetTable) {
      try {
        const suggestions = await this.mappr.suggestMappings({
          sourceFields: Object.keys(data),
          targetTable: mapping.targetTable,
          confidenceThreshold: mapping.confidenceThreshold || 0.8
        });
        
        // Apply high-confidence mappings
        const mappedData: any = {};
        for (const [sourceField, suggestion] of Object.entries(suggestions)) {
          if ((suggestion as any).confidence >= (mapping.confidenceThreshold || 0.8)) {
            mappedData[(suggestion as any).targetField] = data[sourceField];
          }
        }
        
        return mappedData;
      } catch (error) {
        this.log('error', 'Auto-mapping failed', error);
      }
    }
    
    // Use explicit mappings
    if (mapping.fieldMappings) {
      const mappedData: any = {};
      for (const [sourceField, targetField] of Object.entries(mapping.fieldMappings)) {
        if (data[sourceField] !== undefined) {
          mappedData[targetField] = data[sourceField];
        }
      }
      return mappedData;
    }
    
    return data;
  }
  
  /**
   * Tag data using Taggr
   */
  private async tagData(tagging: TaggingConfig, data): Promise<string[]> {
    if (!this.taggr) {
      this.log('warn', 'Taggr not configured, skipping tagging');
      return [];
    }
    
    const tags: string[] = [];
    
    // Auto-tag based on rules
    if (tagging.autoTag && tagging.rules) {
      for (const rule of tagging.rules) {
        if (this.evaluateCondition(rule.condition, data)) {
          tags.push(...rule.tags);
        }
      }
    }
    
    // Add user-provided tags (if enabled)
    if (tagging.userTaggable && data._userTags) {
      tags.push(...data._userTags);
    }
    
    return [...new Set(tags)]; // Remove duplicates
  }
  
  /**
   * Route data to destinations
   */
  private async routeData(
    routing,
    data,
    tags: string[],
    formId: string
  ): Promise<RouteResult[]> {
    const results: RouteResult[] = [];
    
    // Route to data table
    if (routing.dataTable?.enabled && this.actr) {
      try {
        const result = await this.actr.create(routing.dataTable.table, {
          ...data,
          _tags: tags,
          _formId: formId,
          _submittedAt: Date.now()
        });
        
        results.push({
          destination: 'dataTable',
          success: true,
          result
        });
      } catch (error) {
        results.push({
          destination: 'dataTable',
          success: false,
          error: error.message
        });
      }
    }
    
    // Route to external API
    if (routing.external?.enabled && this.integratr) {
      try {
        const transformedData = routing.external.transform
          ? routing.external.transform(data)
          : data;
        
        const result = await this.integratr.call({
          endpoint: routing.external.endpoint,
          method: routing.external.method || 'POST',
          data: transformedData,
          headers: routing.external.headers
        });
        
        results.push({
          destination: 'external',
          success: true,
          result
        });
      } catch (error) {
        results.push({
          destination: 'external',
          success: false,
          error: error.message
        });
      }
    }
    
    // Route to script
    if (routing.script?.enabled && this.actr) {
      try {
        const result = await this.actr.execute('scriptr:execute-script', {
          scriptId: routing.script.scriptId,
          context: { formData: data, tags }
        });
        
        results.push({
          destination: 'script',
          success: true,
          result
        });
      } catch (error) {
        results.push({
          destination: 'script',
          success: false,
          error: error.message
        });
      }
    }
    
    // Route to webhook
    if (routing.webhook?.enabled && this.integratr) {
      try {
        const result = await this.integratr.sendWebhook({
          url: routing.webhook.url,
          event: `form.submitted.${formId}`,
          data: { formData: data, tags },
          signature: routing.webhook.secret ? {
            algorithm: 'sha256',
            secret: routing.webhook.secret
          } : undefined
        });
        
        results.push({
          destination: 'webhook',
          success: true,
          result
        });
      } catch (error) {
        results.push({
          destination: 'webhook',
          success: false,
          error: error.message
        });
      }
    }
    
    return results;
  }
  
  /**
   * Validate a single field
   */
  private validateField(config: FormConfig, fieldId: string, value): string | null {
    const field = config.fields.find(f => f.id === fieldId);
    if (!field || !field.validation) {
      return null;
    }
    
    const validation = field.validation;
    
    // Required
    if (validation.required && (value === undefined || value === null || value === '')) {
      return `${field.label} is required`;
    }
    
    // Skip other validations if empty and not required
    if (!value) {
      return null;
    }
    
    // Min length
    if (validation.minLength && String(value).length < validation.minLength) {
      return `${field.label} must be at least ${validation.minLength} characters`;
    }
    
    // Max length
    if (validation.maxLength && String(value).length > validation.maxLength) {
      return `${field.label} must be at most ${validation.maxLength} characters`;
    }
    
    // Min value
    if (validation.min !== undefined && Number(value) < validation.min) {
      return `${field.label} must be at least ${validation.min}`;
    }
    
    // Max value
    if (validation.max !== undefined && Number(value) > validation.max) {
      return `${field.label} must be at most ${validation.max}`;
    }
    
    // Pattern
    if (validation.pattern) {
      const pattern = typeof validation.pattern === 'string'
        ? new RegExp(validation.pattern)
        : validation.pattern;
      if (!pattern.test(String(value))) {
        return `${field.label} format is invalid`;
      }
    }
    
    // Email
    if (validation.email) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(String(value))) {
        return `${field.label} must be a valid email`;
      }
    }
    
    // URL
    if (validation.url) {
      try {
        new URL(String(value));
      } catch {
        return `${field.label} must be a valid URL`;
      }
    }
    
    // Custom validation
    if (validation.custom) {
      const result = validation.custom(value);
      if (typeof result === 'string') {
        return result;
      }
      if (result === false) {
        return `${field.label} is invalid`;
      }
    }
    
    return null;
  }
  
  /**
   * Get default data from form config
   */
  private getDefaultData(config: FormConfig): Record<string, any> {
    const data: Record<string, any> = {};
    for (const field of config.fields) {
      if (field.defaultValue !== undefined) {
        data[field.id] = field.defaultValue;
      }
    }
    return data;
  }
  
  /**
   * Evaluate tagging condition
   */
  private evaluateCondition(condition: string, data): boolean {
    try {
      // Simple condition evaluation
      // In production, use a proper expression parser
      const fn = new Function('data', `with(data) { return ${condition}; }`);
      return fn(data);
    } catch (error) {
      this.log('error', 'Condition evaluation failed', { condition, error });
      return false;
    }
  }
  
  /**
   * Register Actr actions (Hybrid pattern)
   */
  private registerActions(): void {
    if (!this.actr) return;
    
    // Actions
    this.actr.registerAction('formr:submit-form', async (params) => {
      return this.submitForm(params.formId);
    });
    
    this.actr.registerAction('formr:validate-form', async (params) => {
      return this.validateForm(params.formId);
    });
    
    // CRUD for forms
    this.actr.registerCRUD('form', {
      create: async (data) => this.createForm(data.id, data.config),
      read: async (id: string) => this.getForm(id),
      update: async (id: string, data) => this.updateForm(id, data),
      delete: async (id: string) => this.deleteForm(id),
      list: async () => this.listForms()
    });
    
    this.log('info', 'Actr actions registered');
  }
  
  /**
   * Logging helper
   */
  private log(level: string, message: string, data?: any): void {
    if (this.loggr) {
      this.loggr[level](`[Formr] ${message}`, data);
    } else if (level === 'error') {
      console.error(`[Formr] ${message}`, data);
    }
  }
  
  /**
   * Export form as JSON
   */
  exportForm(formId: string): string {
    const form = this.forms.get(formId);
    if (!form) {
      throw new Error(`Form not found: ${formId}`);
    }
    return JSON.stringify(form.config, null, 2);
  }
  
  /**
   * Import form from JSON
   */
  importForm(json: string): Form {
    const config = JSON.parse(json) as FormConfig;
    return this.createForm(config.id, config);
  }
}

// Singleton instance
let formrInstance: FormrManager | null = null;

export function getFormrInstance(config?: FormrConfig, events?: FormrEvents): FormrManager {
  if (!formrInstance) {
    formrInstance = new FormrManager(config, events);
  }
  return formrInstance;
}

export function resetFormrInstance(): void {
  formrInstance = null;
}
